import { CSSObject } from '@emotion/react';
import useBreakpoints from '@hooks/useBreakpoints';
import useTheme from '@hooks/useTheme';
import useCardStyles from '@cvent/blocks/hooks/useCardStyles';

const useStyles = (
  hasSingleSpeaker?: boolean,
  imageSrc?: string,
  showVirtualRoom?: boolean,
  enhancedSessionView?: boolean,
  isStudioView?: boolean
): Record<string, CSSObject> => {
  const {
    font,
    size,
    transition: { duration }
  } = useTheme();
  const cardStyles = useCardStyles();

  const { mediaIsMobile, mediaIsSmallAndBelow, isMobileOrTablet, isMobile, isS, isM, isL, isXL } = useBreakpoints();

  let headerTitlePaddingTop = 15;
  if (!isMobileOrTablet) {
    headerTitlePaddingTop = 25;
  } else if (isMobile) {
    headerTitlePaddingTop = 5;
  }

  let titleBlockFlexDirection: CSSObject['flexDirection'];
  if (isS) {
    titleBlockFlexDirection = 'column-reverse';
  }

  const speakerWidth = hasSingleSpeaker || isMobileOrTablet ? '100%' : '50%';

  let cardBackLinkMarginBottom = !imageSrc && !showVirtualRoom ? 90 : 32;
  if (isMobileOrTablet) {
    cardBackLinkMarginBottom = 0;
  }

  let speakerNameFontSize = font.base.size['9'];
  if (isMobile) {
    speakerNameFontSize = font.base.size['5'];
  } else if (isM) {
    speakerNameFontSize = font.base.size['7'];
  }

  const backLinkSpacing = () => {
    return isL || isXL ? { paddingLeft: 32, paddingRight: 32 } : { marginLeft: 24, marginRight: 'auto' };
  };

  return {
    categoryAndCalendar: {
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'unset',
      alignItems: isMobile ? 'left' : 'center',
      paddingTop: 2
    },
    detailsCardContainer: {
      borderBottomLeftRadius: isMobileOrTablet ? 0 : size.border.radius.fiveX,
      borderBottomRightRadius: isMobileOrTablet ? 0 : size.border.radius.fiveX,
      width: '100%',
      marginTop: isMobile ? 24 : 32
    },
    backLink: {
      display: 'flex',
      paddingTop: headerTitlePaddingTop + 5,
      paddingBottom: 10,
      justifyContent: isMobile ? 'flex-start' : 'flex-end',
      textDecoration: 'underline'
    },
    detailsBanner: {
      background: '#000',
      borderRadius: '5px',
      display: 'flex',
      height: '160px',
      justifyContent: 'center',
      alignItems: 'center'
    },
    detailsContainer: {
      display: 'flex',
      justifyContent: 'center',
      transition: duration.default,
      'div[data-layout="col"]': {
        transition: duration.default
      },
      maxWidth: 'auto',
      ...(enhancedSessionView
        ? {
            position: 'relative',
            minWidth: '100%',
            marginTop: isStudioView ? 0 : 'auto',
            marginBottom: isStudioView ? 0 : 'auto'
          }
        : {}),
      ...(isStudioView ? { height: '100%' } : {})
    },
    eventContainer: {
      width: '100%',
      padding: `0 ${isMobileOrTablet ? '24' : '32'}px`,
      maxWidth: 884,
      flex: 1
    },
    eventInformation: {
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row'
    },
    dateStyleContainer: {
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      alignItems: !isMobile ? 'baseline' : '',
      flexWrap: 'wrap',
      marginTop: 24
    },
    divider: {
      display: isMobile ? 'none' : 'block',
      marginLeft: 16,
      marginRight: 16
    },
    includeText: {
      fontSize: font.base.size.m,
      marginTop: isMobile ? 8 : 0
    },
    dateStyle: {
      fontSize: font.base.size.m,
      paddingBottom: 0
    },
    detailsContent: {
      display: 'flex',
      margin: '0',
      marginLeft: 'auto',
      marginRight: 'auto',
      paddingBottom: isMobileOrTablet ? 45 : 80,
      padding: '0',
      maxWidth: 1417
    },
    titleBlock: {
      display: 'flex',
      justifyContent: 'space-between',
      paddingTop: '8px',
      paddingBottom: 0,
      width: '100%',
      flexDirection: titleBlockFlexDirection,
      alignItems: 'center'
    },
    cardBackLink: {
      marginTop: isMobileOrTablet ? 25 : 40,
      marginBottom: cardBackLinkMarginBottom,
      marginLeft: isMobileOrTablet ? 16 : 24,
      lineHeight: 1.875
    },
    sessionTitle: {
      margin: 0,
      wordBreak: 'normal',
      overflowWrap: 'anywhere',
      hyphens: 'auto',
      // legacy hyphenate limit for Safari
      WebkitHyphenateLimitBefore: '3',
      WebkitHyphenateLimitAfter: '4'
    },
    subTitle: {
      fontSize: font.base.size.h2,
      fontWeight: font.base.weight.regular,
      padding: '15px 0 43px 0',
      width: '100%',
      marginTop: 0,
      marginBottom: 0
    },
    section: {
      display: 'flex',
      fontSize: font.base.size.s,
      paddingBottom: '5px',
      width: '100%'
    },
    speakerStyle: {
      display: 'flex',
      alignItems: 'normal',
      padding: '5px 25px 5px 0',
      flexWrap: 'wrap',
      width: speakerWidth,
      maxWidth: !isMobileOrTablet && !hasSingleSpeaker ? 575 : ''
    },
    speakerName: {
      fontSize: speakerNameFontSize,
      fontWeight: font.base.weight.bold,
      lineHeight: font.lineHeight.base,
      margin: 0
    },
    speakerTitleCompany: {
      fontSize: isMobile ? font.base.size['4'] : font.base.size['6'],
      lineHeight: font.lineHeight.prose
    },
    speakerInfoWrap: {
      display: 'flex',
      alignItems: 'normal',
      width: '100%',
      paddingBottom: 25
    },
    speakerImageContainer: {
      marginRight: '24px'
    },
    wrap: {
      flexWrap: 'wrap'
    },
    bold: {
      fontWeight: font.base.weight.bold
    },
    flexAuto: {
      flex: 'auto'
    },
    flexFlowColumn: {
      flexFlow: 'column'
    },
    fullWidth: {
      width: '100%'
    },
    sessionDescription: {
      fontSize: isMobile ? font.base.size.m : font.base.size[5],
      lineHeight: font.lineHeight.prose,
      maxWidth: 900,
      marginTop: isMobile ? 48 : 24,
      overflowWrap: 'break-word',
      whiteSpace: 'pre-wrap',
      // Since we are using dangerouslySetInnerHTML, we need to apply these to the underlying elements
      '& p, & span': {
        marginTop: 0,
        fontSize: isMobile ? font.base.size.s : font.base.size[6],
        lineHeight: font.lineHeight.prose
      },
      '& p:last-child': {
        marginBottom: 0
      },
      '& a': {
        color: font.color.interactive.base,
        textDecoration: 'underline',
        '&:hover': {
          color: font.color.interactive.hover,
          textDecoration: 'underline',
          svg: {
            fill: font.color.interactive.hover
          }
        },
        '&:focus': {
          outline: 'none'
        },
        '&:active': {
          color: font.color.interactive.active,
          svg: {
            fill: font.color.interactive.active
          }
        }
      }
    },
    headerTitle: {
      display: 'flex',
      paddingTop: headerTitlePaddingTop,
      paddingBottom: 5,
      justifyContent: 'space-between'
    },
    livePill: {
      marginLeft: 16,
      display: 'flex',
      alignItems: 'center',
      '&:before': {
        content: '"\\00a0"',
        width: '0px',
        visibility: 'hidden',
        fontSize: font.base.size.h1,
        // TODO - Remove in HUB-149332
        [mediaIsSmallAndBelow]: { fontSize: '1.75rem' }
      }
    },
    externalLinkAndShowPasswordContainer: {
      width: 'fit-content',
      marginLeft: 'auto',
      marginRight: 0
    },
    qnaSpacingMobile: {
      marginTop: isMobileOrTablet ? '16px' : '0',
      padding: isMobileOrTablet ? '0 8px' : '0'
    },
    startStreamModal: {
      title: {
        fontSize: font.base.size.h2,
        marginLeft: -8,
        marginRight: -8
      },
      container: {
        marginLeft: 24,
        marginRight: 24
      },
      bodyContent: {
        fontSize: font.base.size.m,
        paddingBottom: 25
      }
    },
    speakerSidebar: {
      paddingLeft: 22,
      paddingRight: 22,
      textAlign: 'center'
    },
    speakerSidebarTitle: {
      fontSize: font.base.size.l,
      lineHeight: font.lineHeight.prose,
      display: '-webkit-box',
      overflow: 'hidden',
      justifyContent: 'center'
    },
    speakerSidebarCompany: {
      fontSize: font.base.size.l,
      lineHeight: font.lineHeight.prose,
      display: '-webkit-box',
      overflow: 'hidden',
      justifyContent: 'center'
    },
    sidebarAvatarContainer: {
      marginBottom: 16,
      position: 'relative',
      left: '50%',
      transform: 'translateX(-85px)' // 85px = half of avatar image width
    },
    socialMediaContainer: {
      display: 'flex',
      marginTop: 8
    },
    speakersGrid: {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'flex-start',
      justifyContent: 'space-between'
    },

    // Sponsors Sections
    sponsorsWrapper: {
      paddingBottom: '48px',
      justifyContent: 'center',
      display: 'flex'
    },
    sessionDetailsSponsorCardWrapper: {
      padding: isS ? '25px 27px 25px' : '40px 42px 39px',
      width: '100%',
      minHeight: 500,
      display: 'flex',
      justifyContent: 'space-between',
      flexDirection: 'column'
    },
    sponsorsHeading: {
      margin: 0,
      paddingTop: 56,
      paddingBottom: 16,
      paddingLeft: 25
    },
    sponsorHeaderWrapper: {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      flexDirection: 'column',
      height: 'auto'
    },
    sessionDetailsSponsorHeading: {
      fontSize: font.base.size['8'],
      textAlign: 'center',
      margin: '8px 0'
    },
    sponsorsDetailsDescription: {
      fontSize: font.base.size[4],
      lineHeight: font.lineHeight.base,
      marginTop: 20,
      textAlign: 'center'
    },
    sponsorFooterWrapper: {
      paddingTop: 26,
      whiteSpace: 'nowrap'
    },
    sessionDetailsFeeContainer: {
      fontSize: font.base.size[5],
      marginTop: 20,
      marginBottom: -16,
      [mediaIsMobile]: {
        fontSize: font.base.size.m
      }
    },
    sessionDetailActionsContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      marginTop: 8,
      marginLeft: -16
    },
    sessionDetailActionButtonContainer: {
      display: 'flex',
      whiteSpace: 'nowrap',
      marginLeft: 16,
      marginTop: 16
    },
    sessionDetailActionButtonContainerNoMargin: {
      display: 'flex',
      whiteSpace: 'nowrap',
      margin: 0
    },
    sessionTitleContainer: {
      display: 'flex',
      alignItems: 'flex-start',
      width: isMobile ? '100%' : 'auto'
    },
    sessionDetailPasswordContainer: {
      display: 'flex',
      whiteSpace: 'nowrap',
      marginLeft: 16,
      marginTop: 28
    },
    sessionDetailPurchasedContainer: {
      color: font.color.soft,
      marginTop: 16,
      marginLeft: 16
    },
    bannerBackLink: {
      marginTop: 24,
      lineHeight: 1.875,
      maxWidth: 1900,
      width: '100%',
      ...backLinkSpacing()
    },
    sessionLocation: {
      fontSize: font.base.size.m,
      paddingBottom: 0,
      marginTop: 8,
      mapPin: {
        display: 'flex',
        alignItems: 'center',
        marginRight: 8
      },
      mapPinLink: {
        paddingRight: 8
      }
    },
    banner: {
      container: {
        padding: 16,
        margin: isMobile ? '24px 24px 0 24px' : '32px 32px 0 32px',
        ...cardStyles
      },
      title: {
        fontSize: isMobile ? font.base.size.m : font.base.size.l,
        marginLeft: isMobile ? 0 : 16,
        marginTop: isMobile ? 8 : 0
      }
    },
    customFieldsTagsLabel: {
      marginTop: 0,
      marginBottom: 16
    },
    featuredRibbonContainer: {
      position: 'relative',
      marginBottom: 6,
      width: 'fit-content'
    },
    duration: {
      marginLeft: 4,
      fontSize: font.base.size.xxs,
      fontWeight: font.base.weight.light
    },
    recommendedSection: {
      display: 'flex',
      alignItems: 'center',
      marginTop: 24,
      text: {
        marginLeft: 14,
        fontSize: font.base.size.m
      }
    },
    sessionTimeSeparator: {
      width: 2,
      height: 2,
      borderRadius: '50%',
      background: font.color.base,
      marginLeft: 8,
      marginRight: 8
    }
  };
};

export default useStyles;
